import { formatIncompletePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { PhoneNumberData, PhoneNumberSearchResult, PhoneNumberCartItem, PhoneNumberCartItemFromAPI } from '../../types/phone-numbers';

// Helper function to capitalize first character
export const capitalizeFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Helper function to format phone number using libphonenumber-js
// Example: "+12133734" with countryISOCode "US" -> "+1 213 373 4"
export const formatPhoneNumber = (
  phoneNumber: string,
  countryISOCode?: string,
): string => {
  if (!phoneNumber) return phoneNumber;

  try {
    // If country code is provided, use it for formatting context
    if (countryISOCode) {
      // Use formatIncompletePhoneNumber which formats with spaces as you type
      // This matches the desired format: "+1 213 373 4"
      const formatted = formatIncompletePhoneNumber(phoneNumber, countryISOCode as any);
      return formatted;
    }

    // If no country code, try to parse and format
    try {
      const parsed = parsePhoneNumber(phoneNumber);
      if (parsed && parsed.isValid()) {
        // Use international format for complete numbers
        return parsed.formatInternational();
      }
    } catch {
      // If parsing fails, use incomplete formatting
    }

    // Fallback to incomplete formatting without country
    return formatIncompletePhoneNumber(phoneNumber);
  } catch (error) {
    // If all formatting fails, return original number
    return phoneNumber;
  }
};

// Helper function to format price: "1.15" -> "$1.15"
export const formatPrice = (price: string): string => {
  if (!price) return price;
  const numPrice = parseFloat(price);
  if (Number.isNaN(numPrice)) return price;
  return `$${numPrice.toFixed(2)}`;
};

// Helper function to format region: combine locality, region, and countryCode
export const formatRegion = (locality: string, region: string, countryCode: string): string => {
  const parts = [locality, region].filter(Boolean);
  return parts.length > 0 ? `${parts.join(' ')}, ${countryCode}` : countryCode;
};

// Transform Redux phone numbers data to display format
export const transformPhoneNumbersData = (
  phoneNumbersList: PhoneNumberSearchResult[] | null,
): PhoneNumberData[] => {
  if (!phoneNumbersList || phoneNumbersList.length === 0) {
    return [];
  }

  return phoneNumbersList.map((item) => ({
    phoneNumber: formatPhoneNumber(item?.phoneNumber, item?.countryCode),
    formattedRegion: formatRegion(item?.locality, item?.region, item?.countryCode),
    region: item?.region,
    locality: item?.locality,
    numberType: capitalizeFirst(item?.numberType),
    addressRequirement: capitalizeFirst(item?.addressRequirement),
    pricePerQuarter: formatPrice(item?.pricePerQuarter),
    countryISOCode: item?.countryCode,
  }));
};

// Transform API cart items to PhoneNumberCartItem format
export const transformCartItemsFromAPI = (
  cartItems: PhoneNumberCartItemFromAPI[] | null,
): PhoneNumberCartItem[] => {
  if (!cartItems || cartItems.length === 0) {
    return [];
  }

  return cartItems.map((item) => ({
    id: item?.id,
    phoneNumber: formatPhoneNumber(item?.phoneNumber, item?.countryCode),
    formattedRegion: formatRegion(item?.locality || '', item?.region || '', item?.countryCode),
    region: item?.region || null,
    locality: item?.locality || null,
    numberType: capitalizeFirst(item?.numberType),
    pricePerQuarter: formatPrice(item?.pricePerQuarter),
    countryISOCode: item?.countryCode,
    isFreeNumber: false,
  }));
};

